/**
 * Раздел «Программы» на aisearch.ru: наш софт для компьютера (02.10.2026).
 *
 * Раньше о Конспекте рассказывали в двух местах: большим блоком на
 * /audio-to-text и английской страницей konspekt.aisearch.ru (GitHub Pages
 * из репозитория программы). Первое размывало тему страницы онлайн-
 * расшифровки, второе — отдельный домен без веса aisearch.ru и не на языке
 * покупателя. Решение владельца: один раздел на основном домене, у каждой
 * программы своя посадочная.
 *
 * Только русская витрина, как раздел для бизнеса и покупка лицензий: продажа
 * идёт через РФ-контур, а тексты написаны для российского покупателя.
 *
 * Все утверждения о программе — из ответа разработчика Конспекта (сессия
 * «Конспект разработка», 02.10 и 03.10, выпуск 0.14.0) по коду и релизам.
 * Чего нет — не обещаем: macOS и Linux, работу на видеокарте, экспорт в
 * docx/pdf, amoCRM и запись итогов в CRM.
 *
 * YouTube по ссылке до 0.15 считался ненадёжным и не обещался. 12.10.2026
 * он обещан на /programmy/konspekt/video-v-tekst: владелец проверил его
 * сам в 0.15.4. В программе нет списка сайтов, ссылка уходит в yt-dlp, и
 * YouTube может сломаться до новой версии — страница говорит об этом прямо.
 */

import { PUT_GOLOSOVOGO_VVODA, TEKST_GOLOSOVOGO_VVODA } from '@/lib/golosovoy-vvod';

/** Последний выпуск программы: GitHub отдаёт свежий установщик сам. */
export const KONSPEKT_SKACHAT = 'https://github.com/lamver/konspekt-releases/releases/latest';

/** Исходный код и текст лицензии кода. */
export const KONSPEKT_KOD = 'https://github.com/lamver/konspekt';

/** Страница покупки лицензии (уже работает, #242). */
export const KONSPEKT_KUPIT = '/pricing/license/konspekt';

/**
 * Вышла ли версия программы, в которой вводится ключ.
 *
 * Ключи, пробные 10 встреч и плашка есть в коде программы, но выпуск с ними
 * (0.12) ещё не вышел: в выпущенной 0.11.0 программа целиком бесплатна и
 * поля для ключа нет. Пока `false`, страница честно говорит «с версии 0.12»
 * и не обещает «введите ключ сейчас». После выхода — переключить `klyuch` в `true` и
 * обновить тексты, где стоит проверка этого признака.
 */
// Свойство объекта, а не голая константа: у константы TypeScript сужает
// тип до false, и линтер считает каждую проверку лишней.
export const VYPUSK_KONSPEKTA: { klyuch: boolean } = { klyuch: true };
export const VERSIYA_S_KLYUCHOM = '0.12';

export interface Programma {
  /** Адрес посадочной внутри раздела. */
  put: string;
  nazvanie: string;
  /** Одна строка для карточки раздела. */
  korotko: string;
  platforma: string;
}

/** Программы раздела. Порядок — порядок карточек. */
export const PROGRAMMY: Programma[] = [
  {
    put: '/programmy/konspekt',
    nazvanie: 'Конспект',
    korotko:
      'Записывает встречи и созвоны, расшифровывает и пишет итог прямо на компьютере. Ни звук, ни текст никуда не отправляются.',
    platforma: 'Windows 10 и 11',
  },
  {
    // Бесплатная часть Конспекта своей посадочной (04.10.2026): человек ищет
    // «программу для голосового ввода», а не функцию программы для встреч.
    put: PUT_GOLOSOVOGO_VVODA,
    nazvanie: 'Голосовой ввод',
    korotko: TEKST_GOLOSOVOGO_VVODA.korotko,
    platforma: 'Windows 10 и 11 · бесплатно',
  },
];

export const TEKST_RAZDELA = {
  // Не «Программы для компьютера»: слишком общий запрос (разбор SEO 02.10).
  title: 'Программы AiSearch для Windows: запись встреч и расшифровка без облака',
  description:
    'Программы AiSearch, которые работают у вас на компьютере: запись встреч, транскрибация аудио и заметки без облака. Windows 10 и 11.',
  h1: 'Программы для компьютера',
  lead: 'То, что удобнее держать у себя, а не в браузере: программы работают на вашем компьютере, и данные с него не уходят.',
  /**
   * Чем программы отличаются от онлайн-инструментов. Страница — хаб: её
   * задача передать вес посадочным, поэтому текст короткий, но со
   * ссылками на соседние инструменты (разбор SEO 02.10).
   */
  abzacy: [
    'На сайте AiSearch много инструментов, которые работают прямо в браузере: например, перевести аудио в текст или распознать текст с фото. Это удобно, когда задача разовая: загрузили файл — получили результат.',
    'Программы — для того, что делается каждый день и не должно уходить с компьютера. Они ставятся на Windows 10 и 11, работают без интернета, а записи, расшифровки и заметки остаются в папке на вашем диске.',
  ],
  ssylki: [
    { href: '/audio-to-text', tekst: 'Аудио в текст онлайн' },
    { href: '/raspoznat-tekst', tekst: 'Распознать текст с фото и PDF' },
  ],
};

/** Ссылка из пункта на свою страницу (голосовой ввод — 04.10.2026). */
export interface SsylkaPunkta {
  href: string;
  tekst: string;
}

interface Punkt {
  zagolovok: string;
  tekst: string;
  ssylka?: SsylkaPunkta;
  /** Есть только с выпуска, где появился ключ (0.12); до него не показывать. */
  sKlyuchom?: boolean;
}

/** Только то, что есть в выпущенной версии (см. VYPUSK_KONSPEKTA). */
function vypushcheno(
  punkty: Punkt[],
): { zagolovok: string; tekst: string; ssylka?: SsylkaPunkta }[] {
  return punkty
    .filter((p) => VYPUSK_KONSPEKTA.klyuch || !p.sKlyuchom)
    .map(({ zagolovok, tekst, ssylka }) =>
      ssylka ? { zagolovok, tekst, ssylka } : { zagolovok, tekst },
    );
}

/**
 * Главные возможности. Сверено разработчиком с кодом 02.10 и 03.10: разбор
 * разговора и расшифровка по ссылке — с 0.12, бот в Telegram — с 0.13,
 * записи из папки и АТС — с 0.14.
 */
export function vozmozhnosti(): { zagolovok: string; tekst: string; ssylka?: SsylkaPunkta }[] {
  return vypushcheno([
    {
      zagolovok: 'Записывает созвоны с обеих сторон',
      tekst:
        'Пишет ваш микрофон и звук компьютера на две дорожки, поэтому собеседник в Zoom, Телемосте и любом другом приложении для звонков тоже записан. Замечает, что из колонок идёт звук — звонок или ролик, — и предлагает включить запись. Работает и как диктофон для Windows: можно просто записать себя.',
      // Статья про запись Телемоста ждала ссылки из текста (Seo-marketing, 16.10.2026).
      ssylka: {
        href: '/blog/telemost-zapis-kuda-sohranyaetsya',
        tekst: 'Где Телемост хранит записи',
      },
    },
    {
      zagolovok: 'Транскрибация на компьютере',
      tekst:
        'Переводит аудио и видео в текст прямо на вашей машине. Русскую речь распознаёт GigaAM от Сбера — модель, сделанная именно для русского языка, другие языки — Whisper. Интернет для этого не нужен.',
    },
    {
      zagolovok: 'Различает говорящих',
      tekst:
        'Каждая реплика подписана, а голоса запоминаются: назовите человека один раз, и на следующих встречах Конспект узнает его сам.',
    },
    {
      zagolovok: 'Пишет итог и протокол встречи',
      tekst:
        'Локальная модель составляет итог встречи: решения, кто что делает, открытые вопросы — по сути, протокол совещания без ручного конспекта. По встрече можно задать вопрос, как в чате.',
    },
    {
      zagolovok: 'Разбирает разговор',
      tekst:
        'Разбор по методикам продаж и переговоров (SPIN, BANT, STAR, один на один) и цифры без модели: доля речи каждого, монологи, перебивания.',
      sKlyuchom: true,
    },
    {
      zagolovok: 'Сам забирает записи звонков',
      tekst:
        'Укажите источник — папку на компьютере (диктофон, облачный диск), сервер своей АТС по SFTP или FTP, Mango Office, UIS (CoMagic) или Битрикс24, — и новые записи сами станут встречами: с номером, сотрудником и временем звонка. Опрос идёт с вашего компьютера, пока программа запущена; нашего сервера в цепочке нет, пароли зашифрованы средствами Windows.',
    },
    {
      zagolovok: 'Свой бот в Telegram',
      tekst:
        'Создайте бота у @BotFather и вставьте токен в настройки. Перешлите ему голосовое, кружок или аудио — Конспект расшифрует на компьютере и пришлёт текст, а с моделью заметок и итог. Бот отвечает, пока программа запущена; файлы до 20 МБ.',
    },
    {
      zagolovok: 'Диктовка в любое окно',
      tekst:
        'Нажмите Ctrl+Shift+D и говорите: текст появится там, где стоит курсор, — в письме, документе или мессенджере.',
      // Голосовой ввод бесплатен и живёт своей посадочной (04.10.2026, Seo-marketing).
      ssylka: { href: PUT_GOLOSOVOGO_VVODA, tekst: 'Голосовой ввод бесплатно' },
    },
    {
      zagolovok: 'Ищет по всем встречам',
      tekst:
        'Поиск по словам и по смыслу во всех записях сразу. Готовые аудиозаписи и видео (mp3, wav, m4a, ogg) можно просто перетащить в окно, а запись по ссылке на VK Видео, Rutube и другие сайты — просто вставить: Конспект их расшифрует.',
      // Видео по ссылке — своя страница (Seo-marketing, 12.10.2026).
      ssylka: { href: '/programmy/konspekt/video-v-tekst', tekst: 'Видео в текст по ссылке' },
    },
  ]);
}

/** Почему локально: доводы для людей и для компаний. */
export function pochemuLokalno(): { zagolovok: string; tekst: string }[] {
  return [
    {
      zagolovok: 'Ничего не уходит с компьютера',
      tekst:
        'Запись, расшифровка и заметки лежат в папке на вашем диске. Чужой сервер их не видит — ни наш, ни сервис распознавания, если вы сами не подключите внешний сервис для заметок.',
    },
    {
      zagolovok: 'Проще с персональными данными',
      tekst:
        'Голоса и разговоры — персональные данные. Когда они не покидают компьютер сотрудника, их не нужно передавать стороннему облаку и описывать такую передачу.',
    },
    {
      zagolovok: 'Работает без интернета',
      tekst:
        'Модели скачиваются один раз: для расшифровки — при первом запуске, для заметок — при первой просьбе. Дальше всё работает без интернета.' +
        (VYPUSK_KONSPEKTA.klyuch ? ' Ключ лицензии тоже проверяется на компьютере, без сети.' : ''),
    },
  ];
}

/**
 * Системные требования — только подтверждённое разработчиком по коду 03.10
 * (Seo-marketing и «Конспект разработка»). Прежние «2 ядра / 4 ГБ / 3 ГБ /
 * 230 МБ на час» в коде и README не подтвердились, а 4 ГБ памяти ещё и
 * противоречили моделям заметок. Память — по моделям заметок: быстрая
 * 1,8 ГБ требует от 8 ГБ, умная 2,5 ГБ — от 12 ГБ, мощная 5 ГБ — от 16 ГБ.
 * Диск: распознавание GigaAM ~220 МБ и Whisper small ~250 МБ, плюс модель
 * заметок — от 0,5 до 5,5 ГБ в сумме.
 */
export const TREBOVANIYA: { chto: string; minimum: string; komfortno: string }[] = [
  { chto: 'Система', minimum: 'Windows 10 или 11, 64 бита', komfortno: 'Windows 11' },
  {
    chto: 'Память',
    minimum: '8 ГБ — заметки быстрой моделью',
    komfortno: '12–16 ГБ — умная и мощная модели',
  },
  {
    chto: 'Диск под модели',
    minimum: 'около 0,5 ГБ — распознавание речи',
    komfortno: 'до 5,5 ГБ — с моделью заметок',
  },
  { chto: 'Видеокарта', minimum: 'не нужна', komfortno: 'не нужна' },
];

/** Вопросы и ответы. Они же уходят в разметку FAQPage. */
/**
 * @param cenaOt цена самой дешёвой лицензии из каталога API, уже в виде
 *   «1 490 ₽». Пусто — каталог не ответил: тогда ответ без суммы, а не с
 *   выдуманной. Числа в коде сайта нет — один источник правды.
 */
export function voprosyKonspekta(cenaOt = ''): { vopros: string; otvet: string }[] {
  const proba = VYPUSK_KONSPEKTA.klyuch
    ? 'Первые 10 встреч работают полностью. После них остаются просмотр, поиск и копирование всех записей, а новая запись и заметки откроются с ключом лицензии.'
    : `Сейчас выпущенная версия работает без ограничений. С версии ${VERSIYA_S_KLYUCHOM} без лицензии будут доступны 10 встреч целиком, затем — просмотр, поиск и копирование всех записей.`;
  return [
    {
      vopros: 'Сколько стоит Конспект?',
      otvet: `Лицензия покупается один раз: на 3 года обновлений или навсегда${cenaOt ? `, от ${cenaOt}` : ''}. Подписки нет. ${proba}`,
    },
    {
      vopros: 'Что будет, когда закончатся 3 года обновлений?',
      otvet:
        'Программа продолжит работать. Ключ подходит ко всем версиям, вышедшим за эти 3 года; более новая версия предложит продлить лицензию, а можно остаться на своей.',
    },
    {
      vopros: 'Можно перенести лицензию на другой компьютер?',
      otvet:
        'Да. Одна лицензия — одно рабочее место: перенести ключ на новый компьютер можно, пользоваться им на двух сразу нельзя. Это условие лицензии.',
    },
    {
      vopros: 'Есть версия для Mac или Linux?',
      otvet: 'Пока нет, только Windows 10 и 11 (64 бита).',
    },
    {
      vopros: 'Нужна ли видеокарта?',
      otvet: 'Нет, всё считается на процессоре, видеокарта не нужна.',
    },
    {
      vopros: 'Сколько места и памяти нужно?',
      otvet:
        'Распознавание речи занимает около 0,5 ГБ на диске. Модель заметок добавляет от 1,8 до 5 ГБ: быстрой нужно от 8 ГБ оперативной памяти, умной — от 12 ГБ, мощной — от 16 ГБ. Записи разговоров лежат в папке на вашем диске, их можно удалять и переносить.',
    },
    {
      vopros: 'Почему Windows предупреждает при установке?',
      otvet:
        'У установщика пока нет платной подписи разработчика, поэтому SmartScreen показывает предупреждение. Нажмите «Подробнее» → «Выполнить в любом случае». Подлинность файла можно проверить по SHA256SUMS со страницы выпуска, а каждый установщик перед публикацией проходит проверку VirusTotal.',
    },
    {
      vopros: 'Это открытая программа?',
      otvet:
        'Исходный код открыт под лицензией FSL-1.1-MIT: его можно читать, менять и собирать самому, некоммерческие и внутренние доработки разрешены. Запрещено одно — делать на его основе конкурирующий коммерческий продукт. Каждая версия через два года становится MIT.',
    },
  ];
}

export const TEKST_KONSPEKTA = {
  // Категория первой, «Конспект» рядом: запрос «конспект встречи» почти
  // целиком про функцию Телемоста, а «конспект» — школьный (разбор SEO 02.10).
  title: 'Программа для транскрибации аудио и записи встреч на ПК — Конспект',
  description:
    'Программа для Windows 10 и 11: записывает созвоны в Zoom, Телемосте и других приложениях, переводит аудио и видео в текст (GigaAM) и пишет итог. Без облака.',
  nadzagolovok: 'Конспект — программа для Windows',
  h1: 'Программа для записи встреч и транскрибации — прямо на компьютере',
  lead: 'Конспект — умный блокнот для встреч и созвонов, записи которых не покидают ваш компьютер. Записывает разговор, переводит аудио и видео в текст и пишет итог целиком на вашей машине.',
  podKnopkoj: VYPUSK_KONSPEKTA.klyuch
    ? 'Windows 10 и 11 · 10 встреч бесплатно · исходный код открыт'
    : 'Windows 10 и 11 · ставится за минуту · исходный код открыт',
};

/**
 * Промо-карточка Конспекта на главной (components/aisearch/promo-konspekta).
 * Те же факты, что на посадочной, коротко; без цены — она в каталоге API.
 */
export const PROMO_GLAVNOJ = {
  nadzagolovok: 'Программа для Windows',
  zagolovok: 'Конспект: запись встреч и транскрибация прямо на компьютере',
  punkty: [
    'Записывает созвоны с обеих сторон — в Zoom, Телемосте и любом другом приложении',
    // Без названия модели: «GigaAM» покупателю ничего не говорит (правка владельца).
    'Хорошо понимает русскую речь и другие языки, сам пишет итог: решения и задачи',
    'Записи и текст остаются на вашем компьютере, интернет для работы не нужен',
  ],
  knopka: 'Подробнее о Конспекте',
  podKnopkoj: 'Windows 10 и 11 · 10 встреч бесплатно · исходный код открыт',
  altSnimka: 'Конспект: итог встречи — решения, ответственные и открытые вопросы',
};
